import { ReactNode, useEffect, useState } from "react";
import HeroEyebrow from "@/components/HeroEyebrow";
import PageHeroBackground from "@/components/PageHeroBackground";
import { computeHeroFilter, readCachedHeroFilter, NEUTRAL_HERO_FILTER } from "@/lib/autoHeroTone";

type Alignment = "center" | "left";

interface BasePageHeroProps {
  /** Optional eyebrow text rendered above the H1. */
  eyebrow?: ReactNode;
  /** Optional icon shown inside the eyebrow chip. */
  eyebrowIcon?: ReactNode;
  /** Required H1. */
  title: ReactNode;
  /** Optional standfirst paragraph beneath the H1. */
  subtitle?: ReactNode;
  /** Optional CTA / chip / breadcrumb row rendered beneath the subtitle. */
  children?: ReactNode;
  /** Alignment for the inner content column. Defaults to "center". */
  align?: Alignment;
  /** Element to render before the children block (e.g. back link on article pages). */
  topSlot?: ReactNode;
}

interface AmbientMediaPageHeroProps extends BasePageHeroProps {
  /**
   * "ambient" pulls a deterministic photo from the library via PageHeroBackground.
   * Used by simple content pages (About, Book, Gallery, etc).
   */
  media: "ambient";
  /** Stable seed for the deterministic photo picker. */
  seed: string;
  /** Route the photo is shown on (usage tracking). */
  route: string;
  /** Human label for usage tracking. */
  pageLabel: string;
  /** Overlay strength preset. */
  overlay?: "default" | "strong";
}

interface ImageMediaPageHeroProps extends BasePageHeroProps {
  /** "image" uses one or more explicit hero images. Multiple = auto-fading slider. */
  media: "image";
  imageSrc: string | string[];
  imageAlt?: string;
  /** Overlay strength preset. */
  overlay?: "default" | "strong";
  /** Slider crossfade interval in ms. Defaults to 6000. */
  slideIntervalMs?: number;
  /** Optional looping muted video; when present it replaces the photo slider. */
  videoSrc?: string | undefined;
  /** Optional poster image shown before the video plays / for reduced-motion users. */
  videoPoster?: string | undefined;
}

type PageHeroProps = AmbientMediaPageHeroProps | ImageMediaPageHeroProps;

/**
 * Shared hero shell for every non-homepage route.
 *
 * Guarantees consistent vertical rhythm across the site:
 *   - Same top padding to clear the fixed nav (h-20 → lg:h-[120px])
 *   - Same bottom padding
 *   - Same min-height
 *   - Same overlay treatment
 *   - Same eyebrow → H1 → subtitle → children stack
 *
 * Pages choose their *media* (ambient library photo OR explicit image), but
 * never their own padding / sizing / structure.
 */
const PageHero = (props: PageHeroProps) => {
  const align: Alignment = props.align ?? "center";
  const overlay = props.overlay ?? "default";

  const overlayClass =
    overlay === "strong"
      ? "bg-gradient-to-r from-charcoal/85 via-charcoal/65 to-charcoal/30"
      : "bg-gradient-to-r from-charcoal/75 via-charcoal/50 to-charcoal/15";

  const alignClasses =
    align === "center"
      ? "text-center mx-auto items-center"
      : "text-left items-start";

  return (
    <section
      className="relative overflow-hidden bg-charcoal pt-12 sm:pt-16 lg:pt-24 pb-20 sm:pb-24 lg:pb-28 min-h-[60vh] lg:min-h-[62vh] flex items-center"
      aria-label="Page header"
    >
      {props.media === "ambient" ? (
        <PageHeroBackground
          seed={props.seed}
          route={props.route}
          pageLabel={props.pageLabel}
          overlay={overlay}
        />
      ) : props.videoSrc ? (
        <HeroVideoLayer
          videoSrc={props.videoSrc}
          posterSrc={props.videoPoster ?? (Array.isArray(props.imageSrc) ? (props.imageSrc[0] ?? "") : props.imageSrc)}
          fallbackImages={Array.isArray(props.imageSrc) ? props.imageSrc : [props.imageSrc]}
          imageAlt={props.imageAlt ?? ""}
          overlayClass={overlayClass}
          slideIntervalMs={props.slideIntervalMs ?? 6000}
        />
      ) : (
        <HeroImageLayer
          imageSrc={props.imageSrc}
          imageAlt={props.imageAlt ?? ""}
          overlayClass={overlayClass}
          slideIntervalMs={props.slideIntervalMs ?? 6000}
        />
      )}

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div
          className={`flex flex-col gap-5 max-w-3xl ${alignClasses}`}
          style={{ textShadow: "0 2px 18px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.55)" }}
        >
          {props.topSlot ? <div className="w-full">{props.topSlot}</div> : null}
          {props.eyebrow ? (
            <HeroEyebrow icon={props.eyebrowIcon}>{props.eyebrow}</HeroEyebrow>
          ) : null}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-charcoal-foreground leading-tight text-balance">
            {props.title}
          </h1>
          {props.subtitle ? (
            <p className="text-base sm:text-xl text-charcoal-foreground/95 max-w-2xl">
              {props.subtitle}
            </p>
          ) : null}
          {props.children ? <div className="w-full">{props.children}</div> : null}
        </div>
      </div>
    </section>
  );
};

interface HeroImageLayerProps {
  imageSrc: string | string[];
  imageAlt: string;
  overlayClass: string;
  slideIntervalMs: number;
}

const HeroImageLayer = ({ imageSrc, imageAlt, overlayClass, slideIntervalMs }: HeroImageLayerProps) => {
  const images = Array.isArray(imageSrc) ? imageSrc.filter(Boolean) : [imageSrc];
  const [index, setIndex] = useState(0);
  // Per-image auto-tone CSS filter. Starts at cached value (if any) or neutral,
  // then upgrades to the analyzed value once the tiny sampling pass resolves.
  const [filters, setFilters] = useState<string[]>(() =>
    images.map((src) => readCachedHeroFilter(src) ?? NEUTRAL_HERO_FILTER),
  );

  useEffect(() => {
    if (images.length < 2) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, slideIntervalMs);
    return () => window.clearInterval(id);
  }, [images.length, slideIntervalMs]);

  useEffect(() => {
    let cancelled = false;
    // Prioritize the first (LCP) image; kick the rest off in the background.
    images.forEach((src, i) => {
      computeHeroFilter(src).then((f) => {
        if (cancelled) return;
        setFilters((prev) => {
          if (prev[i] === f) return prev;
          const next = prev.slice();
          next[i] = f;
          return next;
        });
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.join("|")]);

  return (
    <div className="absolute inset-0" aria-hidden="true">
      {images.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={i === 0 ? imageAlt : ""}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out"
          style={{
            opacity: i === index ? 1 : 0,
            filter: filters[i] ?? NEUTRAL_HERO_FILTER,
          }}
          width={1920}
          height={1080}
          loading={i === 0 ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : "low"}
        />
      ))}
      <div className={`absolute inset-0 ${overlayClass}`} />
    </div>
  );
};

interface HeroVideoLayerProps {
  videoSrc: string;
  posterSrc?: string;
  fallbackImages: string[];
  imageAlt: string;
  overlayClass: string;
  slideIntervalMs: number;
}

const HeroVideoLayer = ({ videoSrc, posterSrc, fallbackImages, imageAlt, overlayClass, slideIntervalMs }: HeroVideoLayerProps) => {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [filter, setFilter] = useState<string>(() =>
    (posterSrc && readCachedHeroFilter(posterSrc)) || NEUTRAL_HERO_FILTER,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  useEffect(() => {
    if (!posterSrc) return;
    let cancelled = false;
    computeHeroFilter(posterSrc).then((f) => {
      if (!cancelled) setFilter(f);
    });
    return () => {
      cancelled = true;
    };
  }, [posterSrc]);

  // If reduced-motion is preferred, fall back to the photo slider (which itself handles auto-tone).
  if (reducedMotion) {
    return (
      <HeroImageLayer
        imageSrc={fallbackImages}
        imageAlt={imageAlt}
        overlayClass={overlayClass}
        slideIntervalMs={slideIntervalMs}
      />
    );
  }

  return (
    <div className="absolute inset-0" aria-hidden="true">
      <video
        key={videoSrc}
        src={videoSrc}
        poster={posterSrc}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter }}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label={imageAlt || undefined}
      />
      <div className={`absolute inset-0 ${overlayClass}`} />
    </div>
  );
};

export default PageHero;
